import { enqueueModalAtom } from '@/atoms/modalQueueAtoms';
import { SESSION_MERGE_EDUCATION_MODAL_ID } from '@/atoms/session-merge-onboarding-modal-ids';
import { SessionMergeIcon } from '@/components/icons/session-merge-icon';
import { ModalPriority } from '@/types/modal-queue.types';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { toast as sonnerToast } from 'sonner';

/**
 * "Session merge enabled" confirmation toast. Shown once the enable flow
 * completes — after the instructional modal that every enable opens is
 * dismissed. "Learn more" re-opens the instructional modal
 * (`SessionMergeEducationModal`) on demand; enqueueing is deduplicated, so a
 * click while the modal is already queued or visible is a no-op. This path
 * deliberately does not arm `showEnabledToastOnEducationCloseAtom`, so
 * closing that re-opened modal shows no second toast (no toast ↔ modal loop).
 */
export function useSessionMergeEnabledToast(): {
  showSessionMergeEnabledToast: () => void;
} {
  const intl = useIntl();
  const enqueueModal = useSetAtom(enqueueModalAtom);

  const showSessionMergeEnabledToast = useCallback(() => {
    // Callout snackbar: `p-4` matches the roomier 400×60 frame. No close
    // (X) is desired — the "Learn more" action + auto-dismiss handle it.
    // The DS `toast()` helper currently force-sets `onDismiss` (a known
    // migration regression the DS team is fixing); once that lands, no X
    // renders here without any change on our side.
    const toastId = toast({
      className: 'p-4',
      icon: <SessionMergeIcon className="size-4" />,
      title: intl.formatMessage({
        defaultMessage: 'Session merge enabled',
        id: 'Zr0qVBxRBW',
      }),
      action: (
        <Button
          type="button"
          variant="secondary"
          size="xs"
          onClick={() => {
            enqueueModal({
              id: SESSION_MERGE_EDUCATION_MODAL_ID,
              priority: ModalPriority.NORMAL,
              dismissible: true,
            });
            sonnerToast.dismiss(toastId);
          }}
        >
          {intl.formatMessage({
            defaultMessage: 'Learn more',
            id: 'TdTXXf940t',
          })}
        </Button>
      ),
    });
  }, [intl, enqueueModal]);

  return { showSessionMergeEnabledToast };
}
